<template>
  <div class="store-order-form">

    <div v-if="loadingProducts && products.length === 0" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2 text-muted">Loading products…</p>
    </div>

    <div v-else>
      <!-- SUMMARY STRIP -->
      <section class="vs-strip" style="margin-bottom: 16px;">
        <div class="vs-strip-cell">
          <span>Ordering To</span>
          <strong>{{ getStoreName(orderData.ordering_location) || '—' }}</strong>
        </div>
        <div class="vs-strip-cell">
          <span>Ordering From</span>
          <strong>{{ getStoreName(orderData.selling_location) || '—' }}</strong>
        </div>
        <div class="vs-strip-cell">
          <span>Items</span>
          <strong>{{ totalItems }}</strong>
        </div>
        <div class="vs-strip-cell">
          <span>Total</span>
          <strong class="is-good">${{ totalAmount.toFixed(2) }}</strong>
        </div>
      </section>

      <b-tabs v-model="activeTab" card class="order-view-tabs">

        <!-- ================= TAB 1: SEARCH PRODUCTS ================= -->
        <b-tab title="1. Search Products" :title-link-class="'otab is-product'">
          <div style="padding: 16px;">
            <div class="vs-fields" style="grid-template-columns: 1fr 1fr; margin-bottom: 16px;">
              <label class="vs-field">
                <span>Ordering Location (Your Store)</span>
                <b-form-select v-model="orderData.ordering_location" :options="myStoreOptions" class="vs-field-input"></b-form-select>
              </label>
              <label class="vs-field">
                <span>Ordering From (Source)</span>
                <b-form-select v-model="orderData.selling_location" :options="sourceLocationOptions" class="vs-field-input" @change="loadProducts"></b-form-select>
              </label>
            </div>

            <div class="pos-search-row" style="margin-bottom: 16px;">
              <b-input-group class="pos-search-input">
                <b-form-input v-model="searchKeyword" placeholder="Search SKU or product name…"></b-form-input>
                <b-input-group-append v-if="searchKeyword">
                  <b-button variant="outline-secondary" @click="searchKeyword = ''">
                    <b-icon icon="x"></b-icon>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <div v-if="filteredProducts.length > 0" class="product-cards-container" style="padding: 0; max-height: 500px; overflow-y: auto;">
              <div class="product-cards-grid">
                <div
                  v-for="p in filteredProducts"
                  :key="p.product_sku"
                  class="product-card"
                  :class="{ 'out-of-stock': (p.available || 0) <= 0 }"
                >
                  <div class="product-card-info">
                    <div class="product-sku">{{ p.product_sku }}</div>
                    <div class="product-name">{{ p.product_name }}</div>
                    <div class="d-flex flex-wrap mt-2" style="gap: 6px;">
                      <span class="vs-chip">Stock: {{ p.in_stock || 0 }}</span>
                      <span class="vs-chip is-warn">Reserved: {{ p.reserved || 0 }}</span>
                      <span class="vs-chip is-teal">Available: {{ p.available || 0 }}</span>
                    </div>
                  </div>
                  <div class="product-card-actions">
                    <div class="price-row">
                      <span class="price-label">Wholesale</span>
                      <span class="current-price">${{ (parseFloat(p.wholesale_price) || 0).toFixed(2) }}</span>
                    </div>
                    <b-button class="vs-btn-primary" size="sm" block @click="addToCart(p)" :disabled="addingToCart[p.product_sku]">
                      <b-spinner small v-if="addingToCart[p.product_sku]"></b-spinner>
                      <b-icon v-else icon="plus"></b-icon> Add
                    </b-button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="!searchKeyword" class="pos-empty text-center py-5" style="background: #FAF9F7; border-radius: 14px; border: 1.5px dashed #E5E1DA;">
              <b-icon icon="search" font-scale="3" style="color: #C9C2B8;"></b-icon>
              <h4 class="mt-3" style="font-family: 'Sora'; font-size: 18px;">Start typing to search</h4>
              <p class="text-muted mb-0">Search by SKU or product name to add items to your order.</p>
            </div>

            <div v-else class="pos-empty text-center py-5">
              <b-icon icon="inbox" font-scale="3" style="color: #C9C2B8;"></b-icon>
              <p class="mt-2 mb-0">No products found for "{{ searchKeyword }}".</p>
            </div>
          </div>
        </b-tab>

        <!-- ================= TAB 2: ORDER CART ================= -->
        <b-tab :title="'2. Order Cart (' + cart.length + ')'" :title-link-class="'otab is-order'">
          <div style="padding: 16px; background: #FAF9F7; min-height: 400px;">

            <div class="d-flex justify-content-between align-items-center mb-3">
              <h3 style="font-family: 'Sora'; font-size: 18px; font-weight: 700; margin: 0;">Line Item Details</h3>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <b-form-select
                  v-if="isAdmin"
                  v-model="bulkStatus"
                  :options="bulkStatusOptions"
                  size="sm"
                  style="width: 180px;"
                  :disabled="submitting"
                  @change="applyBulkStatus"
                >
                  <template #first>
                    <option value="">Bulk Set Status…</option>
                  </template>
                </b-form-select>
                <span class="vs-chip">{{ cart.length }} rows</span>
              </div>
            </div>

            <div v-if="cart.length === 0" class="text-center text-muted py-5">
              Add items from the "Search Products" tab to edit details.
            </div>

            <div v-else class="ord-product-list">
              <article
                v-for="(item, idx) in cart"
                :key="'row-' + idx"
                class="ord-product-card"
                :class="'is-status-' + item.status"
              >
                <!-- IMAGE -->
                <div class="ord-product-img">
                  <input type="file" accept="image/*" :ref="'fileInput-' + idx"
                    style="display:none" @change="handleImageUpload($event, idx)" />
                  <img v-if="item.images && item.images.length"
                    :src="item.images[0].raw || item.images[0].url"
                    @click="$refs['fileInput-' + idx][0].click()" style="cursor:pointer;" />
                  <div v-else class="ord-product-img-empty" @click="$refs['fileInput-' + idx][0].click()">
                    <b-icon icon="camera" font-scale="2"></b-icon>
                    <span>Upload</span>
                  </div>
                  <b-button v-if="item.images && item.images.length" size="sm" variant="danger"
                    class="ord-product-img-remove" @click.stop="removeImage(idx)">
                    <b-icon icon="x"></b-icon>
                  </b-button>
                </div>

                <!-- INFO -->
                <div class="ord-product-panel ord-product-info">
                  <div class="ord-product-sku">{{ item.product_sku }}</div>
                  <div class="ord-product-name">{{ item.product_name || '—' }}</div>
                  <div class="ord-product-tags">
                    <span class="vs-chip" :class="getStatusTagClass(item.status)">{{ getStatusText(item.status) }}</span>
                    <span class="vs-chip"><b-icon icon="box"></b-icon> {{ item.quantity || 0 }} units</span>
                  </div>
                  <div class="ord-product-sub">
                    <div><b>Wholesale:</b> ${{ (item.wholesale_price || 0).toFixed(2) }}</div>
                    <div><b>Cubes:</b> {{ ((item.cubes || 0) * (item.quantity || 0)).toFixed(2) }}</div>
                  </div>
                  <div class="ord-avail">
                    <div class="ord-avail-item"><span>STK</span><b>{{ item.in_stock || 0 }}</b></div>
                    <div class="ord-avail-item is-warn"><span>RSV</span><b>{{ item.reserved || 0 }}</b></div>
                    <div class="ord-avail-item is-good"><span>AVL</span><b>{{ item.available || 0 }}</b></div>
                  </div>
                </div>

                <!-- QUANTITIES -->
                <div class="ord-product-panel ord-product-qty">
                  <h6 class="ord-panel-title">Quantities</h6>
                  <div class="ord-qty-grid">
                    <label class="ord-qty-field">
                      <span>Qty</span>
                      <b-form-input size="sm" type="number" min="0"
                        v-model.number="item.quantity" @input="onQuantityInput(item)"></b-form-input>
                    </label>
                    <label class="ord-qty-field">
                      <span>Sch Qty</span>
                      <b-form-input size="sm" type="number" min="0" :max="item.quantity"
                        v-model.number="item.quantity_scheduled"
                        @input="onScheduledQuantityInput(item)"
                        @blur="validateScheduledQuantity(item)"></b-form-input>
                    </label>
                    <label class="ord-qty-field">
                      <span>Shipped</span>
                      <b-form-input size="sm" type="number" min="0" :max="item.quantity"
                        v-model.number="item.quantity_shipped"
                        :disabled="item.status !== 2 && item.status !== 6"
                        @blur="validateShippedQuantity(item)"></b-form-input>
                    </label>
                    <label class="ord-qty-field">
                      <span>Rcv Qty</span>
                      <b-form-input size="sm" type="number" min="0" :max="item.quantity_scheduled || item.quantity"
                        v-model.number="item.quantity_received"
                        :disabled="item.status !== 2 && item.status !== 6 && item.status !== 3 && item.status !== 4"
                        @change="validateQuantity(item)"></b-form-input>
                    </label>
                    <label class="ord-qty-field">
                      <span>Item Status</span>
                      <b-form-select size="sm" :options="getStatusOptions(item)"
                        v-model.number="item.status" @change="handleStatusChange(item)"></b-form-select>
                    </label>
                    <label class="ord-qty-field">
                      <span>Del Status</span>
                      <b-form-select size="sm" :options="deliveryStatuses"
                        v-model.number="item.delivery_status"></b-form-select>
                    </label>
                  </div>
                </div>

                <!-- LOCATIONS & DATES -->
                <div class="ord-product-panel ord-product-locs">
                  <h6 class="ord-panel-title">Locations &amp; Dates</h6>
                  <div class="ord-qty-grid">
                    <label class="ord-qty-field">
                      <span>Item Loc (Source)</span>
                      <b-form-select size="sm" :options="item.reservedLocationOptions || []"
                        v-model="item.reserved_locsub"></b-form-select>
                    </label>
                    <label class="ord-qty-field">
                      <span>Receive Loc</span>
                      <b-form-select size="sm" :options="destLocOptions"
                        v-model="item.locsub"></b-form-select>
                    </label>
                    <label class="ord-qty-field">
                      <span>Schedule Date</span>
                      <date-picker v-model="item.schedule_date" format="MM-DD-YYYY"
                        valueType="format" @change="onScheduleDateChange(item, $event)"></date-picker>
                    </label>
                    <label class="ord-qty-field">
                      <span>Request ETA</span>
                      <date-picker v-model="item.requested_delivery_date" format="MM-DD-YYYY"
                        valueType="format" @change="onDeliveryDateChange(item, $event)"></date-picker>
                    </label>
                    <label class="ord-qty-field" style="grid-column: span 2;">
                      <span>Received Date</span>
                      <date-picker v-model="item.received_date" format="MM-DD-YYYY"
                        valueType="format"
                        :disabled-date="disableFutureDates"
                        :disabled="item.status !== 3"
                        @change="onReceivedDateChange(item, $event)"></date-picker>
                    </label>
                  </div>
                </div>

                <!-- PRICE & COMMENTS -->
                <div class="ord-product-panel ord-product-price">
                  <h6 class="ord-panel-title">Pricing &amp; Notes</h6>
                  <label class="ord-qty-field">
                    <span>Wholesale Price</span>
                    <b-form-input size="sm" type="number" step="0.01"
                      v-model.number="item.wholesale_price"></b-form-input>
                  </label>
                
                  <label class="ord-qty-field">
                    <span>Comments</span>
                    <b-form-input size="sm" v-model="item.comments" placeholder="…"></b-form-input>
                  </label>
                  <div style="font-size: 11px; color: #A59D92; font-weight: 600; margin-top: auto;">
                    Line #{{ idx + 1 }}
                  </div>
                    <div class="ord-price-total">
                    <strong>${{ ((item.quantity || 0) * (item.wholesale_price || 0)).toFixed(2) }}</strong>
                  </div>
                  
                </div>

                <!-- ACTIONS -->
                <div class="ord-product-panel ord-product-actions">
                  <b-button size="sm" variant="outline-danger" @click="removeFromCart(idx)" class="w-100">
                    <b-icon icon="trash-fill"></b-icon>
                  </b-button>
                  <b-button size="sm" variant="outline-primary" class="w-100"
                    @click="$refs['fileInput-' + idx][0].click()">
                    <b-icon icon="camera"></b-icon>
                  </b-button>
                </div>
              </article>
            </div>

          </div>

          <!-- STICKY FOOTER -->
          <div class="pos-totals-bar" style="position: sticky; bottom: 0; z-index: 10; margin: 0; border-radius: 0 0 12px 12px;">
            <div class="pos-totals-stats">
              <div class="pos-totals-item">
                <b-icon icon="bag"></b-icon>
                <div><span>Items</span><strong>{{ totalItems }}</strong></div>
              </div>
              <div class="pos-totals-item is-grand">
                <div><span>Total Amount</span><strong>${{ totalAmount.toFixed(2) }}</strong></div>
              </div>
            </div>
            <div class="pos-totals-actions">
              <span class="pos-foot-note" style="margin-right: 16px;">All quantities and dates are editable.</span>
              <b-button class="vs-btn-primary pos-btn-lg" :disabled="submitting || cart.length === 0" @click="submitOrder">
                <b-spinner small v-if="submitting"></b-spinner>
                <b-icon v-else icon="check2-circle"></b-icon>
                {{ submitting ? 'Submitting…' : 'Submit Order' }}
              </b-button>
            </div>
            <b-alert v-if="submitError" show variant="danger" class="mb-0 w-100 mt-2">{{ submitError }}</b-alert>
          </div>

        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: "NewStoreOrderForm",
  components: { DatePicker },
  data() {
    return {
      activeTab: 0,
      loadingProducts: false,
      searchKeyword: '',
      products: [],
      cart: [],
      addingToCart: {},
      submitting: false,
      submitError: null,
      orderData: { ordering_location: null, selling_location: null },
      packet: {},
      userDetails: {},
      bulkStatus: '',
      bulkStatusOptions: [
        { value: 1, text: 'ORDERED' },
        { value: 2, text: 'SCHEDULED' },
        { value: 6, text: 'SHIPPED' },
        { value: 3, text: 'RECEIVED' },
        { value: 4, text: 'PARTIAL' },
        { value: 5, text: 'COMPLETED' },
      ],
      deliveryStatuses: [
        { value: 0, text: 'PENDING' },
        { value: 1, text: 'SCHEDULED' },
        { value: 2, text: 'IN TRANSIT' },
        { value: 3, text: 'DELIVERED' },
      ],
    }
  },
  computed: {
    isAdmin() {
      return this.userDetails && this.userDetails.userrole === 'admin';
    },
    myStoreOptions() {
      const locs = Array.isArray(this.packet.locations) ? this.packet.locations : [];
      const myLocId = this.userDetails.properties ? this.userDetails.properties.location_id : null;
      const filtered = locs.filter(l => {
        const isTargetStore = myLocId ? String(l.value) === String(myLocId) : true;
        if (!isTargetStore) return false;
        const t = (l.text || '').toLowerCase();
        if (t.includes('online') || t.includes('out of stock') || t.includes('mecca shipping')) return false;
        if (['0', '40', '41', '42'].includes(String(l.value))) return false;
        return true;
      });
      return [{ value: 0, text: 'MECCA WHOLESALE' }, ...filtered];
    },
    sourceLocationOptions() {
      const locs = Array.isArray(this.packet.locations) ? this.packet.locations : [];
      const myLocId = this.userDetails.properties ? this.userDetails.properties.location_id : null;
      const filtered = locs.filter(l => {
        if (String(l.value) === String(myLocId)) return false;
        const t = (l.text || '').toLowerCase();
        if (t.includes('online') || t.includes('out of stock') || t.includes('mecca shipping')) return false;
        if (['40', '41', '42'].includes(String(l.value))) return false;
        if (String(l.value) === '0') return false;
        return true;
      });
      return [{ value: 0, text: 'MECCA WHOLESALE' }, ...filtered];
    },
    destLocOptions() {
      const locsubs = Array.isArray(this.packet.locsubs) ? this.packet.locsubs : [];
      if (!this.orderData.ordering_location) return [];
      return locsubs
        .filter(l => {
          if (!String(l.value).startsWith(String(this.orderData.ordering_location) + '|')) return false;
          const locName = (l.text || l.sublocation_name || '').toLowerCase();
          if (locName.includes('damage')) return false;
          return true;
        })
        .map(l => ({ value: l.value, text: l.text }));
    },
    filteredProducts() {
      if (!this.searchKeyword) return [];
      const kw = this.searchKeyword.toLowerCase();
      return this.products.filter(p =>
        (p.product_sku || '').toLowerCase().includes(kw) ||
        (p.product_name || '').toLowerCase().includes(kw)
      );
    },
    totalItems() { return this.cart.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0); },
    totalAmount() {
      return this.cart.reduce((s, i) => s + ((parseFloat(i.quantity) || 0) * (parseFloat(i.wholesale_price) || 0)), 0);
    },
  },
  created() {
    try {
      this.packet = JSON.parse(localStorage.packet || '{}');
      this.userDetails = JSON.parse(localStorage.userDetails || '{}');
    } catch (e) { this.packet = {}; this.userDetails = {}; }

    const myLocId = this.userDetails.properties ? this.userDetails.properties.location_id : null;
    if (myLocId) this.orderData.ordering_location = parseInt(myLocId) || myLocId;
    this.orderData.selling_location = 0;
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    getStoreName(value) {
      if (value === null || value === undefined) return '';
      if (String(value) === '0') return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === String(value));
      return found ? found.text : '';
    },
    async loadProducts() {
      if (this.orderData.selling_location === null) return;
      this.loadingProducts = true;
      this.searchKeyword = '';
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP(
            "/api/product/get_product_list_for_transfer_order/",
            { selling_location: this.orderData.selling_location },
            this, resolve, reject);
        });
        if (res.data && res.data.error) {
          this.$bvToast.toast(res.data.error, { title: 'Error', variant: 'danger' });
          this.products = [];
        } else {
          this.products = Array.isArray(res.data && res.data.result) ? res.data.result : [];
        }
      } catch (e) {
        console.error(e);
        this.products = [];
      } finally {
        this.loadingProducts = false;
      }
    },

    async addToCart(product) {
      if (!product || !product.product_sku) return;

      const existing = this.cart.find(i => i.product_sku === product.product_sku);
      if (existing) { existing.quantity = (parseInt(existing.quantity) || 0) + 1; return; }

      this.$set(this.addingToCart, product.product_sku, true);
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP(
            "/api/product/get_available_inventory_dropdown/",
            { sku: product.product_sku, selling_location: this.orderData.selling_location },
            this, resolve, reject
          );
        });

        let availableLocations = [];
        if (res.data && res.data.result && res.data.result.length > 0) {
          availableLocations = res.data.result.map(l => ({ value: l.value, text: l.text }));
        }
        if (availableLocations.length === 0) {
          if (String(this.orderData.selling_location) === '0') {
            availableLocations = [{ value: '0|0', text: 'MECCA WHOLESALE (Global)' }];
          } else {
            const locsubs = this.packet.locsubs || [];
            availableLocations = locsubs
              .filter(l => String(l.value).startsWith(String(this.orderData.selling_location) + '|'))
              .map(l => ({ value: l.value, text: l.text }));
          }
        }

        const defaultReservedLocsub = availableLocations.length > 0 ? availableLocations[0].value : null;
        const defaultDestLocsub = this.destLocOptions.length > 0 ? this.destLocOptions[0].value : null;

        this.cart.push({
          product_sku: product.product_sku,
          product_name: product.product_name,
          product_id: product.product_id || null,
          wholesale_price: parseFloat(product.wholesale_price) || 0,
          cubes: parseFloat(product.cubes) || 0,
          quantity: 1, quantity_scheduled: 0, quantity_shipped: 0, quantity_received: 0,
          status: 1, original_status: 1, delivery_status: 0,
          schedule_date: null, requested_delivery_date: null, received_date: null,
          reserved_locsub: defaultReservedLocsub,
          locsub: defaultDestLocsub,
          reservedLocationOptions: availableLocations,
          comments: null, images: [],
          in_stock: product.in_stock || 0,
          reserved: product.reserved || 0,
          available: product.available || 0,
        });
      } catch (e) {
        console.error("Error fetching item inventory details:", e);
        this.$bvToast.toast("Error fetching item locations", { variant: 'danger' });
      } finally {
        this.$delete(this.addingToCart, product.product_sku);
      }
    },

    removeFromCart(idx) { this.cart.splice(idx, 1); },
    handleImageUpload(event, idx) {
      const file = event.target.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) { this.$bvToast.toast('Invalid image', { variant: 'warning' }); return; }
      const reader = new FileReader();
      reader.onload = (e) => { this.cart[idx].images = [{ raw: e.target.result }]; };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
    removeImage(idx) { this.cart[idx].images = []; },

    /* ============================================================
     *  STATUS + QUANTITY LOGIC (same as details modal / TransferOrderModal)
     * ============================================================ */
    disableFutureDates(date) { return date > new Date(); },

    getStatusOptions(item) {
      const scheduledQty = parseInt(item.quantity_scheduled) || 0;
      const receivedQty  = parseInt(item.quantity_received)  || 0;
      const isScheduleComplete = scheduledQty > 0;
      return [
        { value: 1, text: "ORDERED",   disabled: false },
        { value: 2, text: "SCHEDULED", disabled: scheduledQty > 0 },
        { value: 6, text: "SHIPPED",   disabled: !isScheduleComplete },
        { value: 3, text: "RECEIVED",  disabled: !isScheduleComplete },
        { value: 4, text: "PARTIAL",   disabled: !isScheduleComplete || receivedQty <= 0 || receivedQty === scheduledQty },
        { value: 5, text: "COMPLETED", disabled: false },
      ];
    },

    handleStatusChange(item) {
      const scheduledQty = parseInt(item.quantity_scheduled) || 0;
      const receivedQty  = parseInt(item.quantity_received)  || 0;
      const originalStatus = item.original_status || item.status;

      if (item.status === 6 && scheduledQty <= 0) {
        this.$bvToast.toast("Cannot mark as SHIPPED: schedule it first.", { variant: 'warning', autoHideDelay: 2500 });
        this.$nextTick(() => { item.status = originalStatus; });
        return;
      }
      if (item.status === 3 && scheduledQty <= 0) {
        this.$bvToast.toast("Cannot mark as RECEIVED: schedule it first.", { variant: 'warning', autoHideDelay: 2500 });
        this.$nextTick(() => { item.status = originalStatus; });
        return;
      }
      if (item.status === 4) {
        if (receivedQty <= 0 || receivedQty >= scheduledQty) {
          this.$bvToast.toast("PARTIAL requires received qty > 0 and < scheduled.", { variant: 'warning', autoHideDelay: 2500 });
          this.$nextTick(() => { item.status = originalStatus; });
          return;
        }
        if (!item.received_date) this.$set(item, 'received_date', moment().format('MM-DD-YYYY'));
      }
      if (item.status === 2) {
        if (!item.schedule_date) this.$set(item, 'schedule_date', moment().format('MM-DD-YYYY'));
        if (!item.quantity_scheduled || item.quantity_scheduled <= 0) {
          this.$set(item, 'quantity_scheduled', item.quantity);
        }
      }
      if (item.status === 3) {
        this.$set(item, 'received_date', moment().format('MM-DD-YYYY'));
        if (!item.quantity_received || item.quantity_received === 0) {
          this.$set(item, 'quantity_received', scheduledQty);
        }
      }
    },

    onQuantityInput(item) {
      const qty = parseInt(item.quantity, 10);
      if (isNaN(qty) || qty <= 0) {
        this.$set(item, 'quantity', 0);
        this.$set(item, 'quantity_shipped', 0);
        return;
      }
      this.$set(item, 'quantity', qty);
      if (item.quantity_scheduled > qty) item.quantity_scheduled = qty;
      if (item.quantity_shipped > qty) item.quantity_shipped = qty;
    },

    onScheduledQuantityInput(item) {
      clearTimeout(this._schedTimer);
      this._schedTimer = setTimeout(() => this.validateScheduledQuantity(item), 300);
    },
    validateScheduledQuantity(item) {
      this.$nextTick(() => {
        if (item.quantity_scheduled < 0) item.quantity_scheduled = 0;
        if (item.quantity_scheduled > item.quantity) item.quantity_scheduled = item.quantity;

        if (item.quantity_scheduled > 0 && item.status !== 3) {
          if (item.status !== 2) {
            item.status = 2;
            if (!item.schedule_date) this.$set(item, 'schedule_date', moment().format('MM-DD-YYYY'));
          }
        } else if (item.quantity_scheduled === 0 && item.status === 2) {
          item.status = 1;
        }
      });
    },
    validateShippedQuantity(item) {
      this.$nextTick(() => {
        let v = Number(item.quantity_shipped) || 0;
        if (v < 0) v = 0;
        if (v > item.quantity) v = item.quantity;
        item.quantity_shipped = v;
      });
    },
    validateQuantity(item) {
      if (item.status === 3) {
        const scheduledQty = parseInt(item.quantity_scheduled) || 0;
        const receivedQty  = parseInt(item.quantity_received)  || 0;
        if (receivedQty > scheduledQty) item.quantity_received = scheduledQty;
        return;
      }
      const scheduledQty = parseInt(item.quantity_scheduled) || 0;
      const max = scheduledQty > 0 ? scheduledQty : item.quantity;
      this.$nextTick(() => {
        let received = Number(item.quantity_received) || 0;
        if (received < 0) received = 0;
        if (received > max) received = max;
        item.quantity_received = received;

        if (received > 0 && scheduledQty > 0) {
          if (received === scheduledQty) {
            const orderedQty = parseInt(item.quantity) || 0;
            if (scheduledQty === orderedQty) {
              item.status = 3;
              if (!item.received_date) item.received_date = moment().format('MM-DD-YYYY');
            } else {
              item.status = 3;
            }
          } else {
            item.status = 4;
            if (!item.received_date) item.received_date = moment().format('MM-DD-YYYY');
          }
        } else {
          item.status = item.original_status || 1;
          item.received_date = null;
        }
      });
    },
    onScheduleDateChange(item, date) {
      if (date && item.status !== 3) {
        this.$nextTick(() => {
          if (!item.quantity_scheduled || item.quantity_scheduled <= 0) {
            this.$set(item, 'quantity_scheduled', item.quantity);
          }
          item.status = 2;
        });
      }
      if (!date && item.status === 2 && (!item.quantity_scheduled || item.quantity_scheduled <= 0)) {
        this.$nextTick(() => { item.status = 1; });
      }
    },
    onDeliveryDateChange(item, date) {
      if (date && item.status !== 3) {
        this.$nextTick(() => { item.status = 2; });
      }
      if (!date && item.status === 2) {
        this.$nextTick(() => { item.status = 1; });
      }
    },
    onReceivedDateChange(item, date) {
      const today = moment().format('MM-DD-YYYY');
      if (date !== today) {
        this.$bvToast.toast("Received date must be today's date.", { variant: 'warning', autoHideDelay: 2000 });
        this.$set(item, 'received_date', today);
        return;
      }
      if (date) {
        const scheduledQty = parseInt(item.quantity_scheduled) || 0;
        const receivedQty  = parseInt(item.quantity_received)  || 0;
        if (scheduledQty <= 0) {
          this.$bvToast.toast("Schedule first.", { variant: 'warning' });
          this.$set(item, 'received_date', null);
          return;
        }
        if (receivedQty > 0 && receivedQty <= scheduledQty) {
          item.status = receivedQty === scheduledQty ? 3 : 4;
        } else {
          this.$set(item, 'received_date', null);
        }
      }
    },

    applyBulkStatus(statusValue) {
      if (!statusValue) return;
      let updated = 0;
      this.cart = this.cart.map(item => {
        item.status = statusValue;
        updated++;
        if (statusValue === 2) {
          const orderedQty = parseInt(item.quantity) || 0;
          if (orderedQty > 0) {
            item.quantity_scheduled = orderedQty;
            if (!item.schedule_date) item.schedule_date = moment().format('MM-DD-YYYY');
          }
        }
        if (statusValue === 6) {
          const orderedQty = parseInt(item.quantity) || 0;
          if (orderedQty > 0 && (!item.quantity_scheduled || item.quantity_scheduled === 0)) {
            item.quantity_scheduled = orderedQty;
          }
        }
        if (statusValue === 3) {
          const orderedQty = parseInt(item.quantity) || 0;
          if (orderedQty > 0) {
            if (!item.quantity_scheduled || item.quantity_scheduled === 0) {
              item.quantity_scheduled = orderedQty;
            }
            item.quantity_received = item.quantity_scheduled || orderedQty;
            item.received_date = moment().format('MM-DD-YYYY');
          }
        }
        return item;
      });
      this.$bvToast.toast(`Applied ${this.getStatusText(statusValue)} to ${updated} items.`,
        { variant: 'success', autoHideDelay: 2500 });
      this.bulkStatus = '';
    },

    getStatusText(status) {
      const map = {1:'ORDERED', 2:'SCHEDULED', 3:'RECEIVED', 4:'PARTIAL', 5:'COMPLETED', 6:'SHIPPED'};
      return map[status] || 'UNKNOWN';
    },
    getStatusTagClass(status) {
      const map = {1:'is-warn', 2:'is-teal', 3:'is-teal', 4:'is-warn', 5:'is-teal', 6:'is-warn'};
      return map[status] || '';
    },

    async submitOrder() {
      this.submitError = null;
      if (!this.orderData.ordering_location || this.orderData.selling_location === null) {
        this.submitError = 'Please select both locations.'; return;
      }
      if (this.orderData.ordering_location === this.orderData.selling_location) {
        this.submitError = 'Locations must differ.'; return;
      }
      if (!this.cart.length) { this.submitError = 'Add at least one product.'; return; }

      for (const item of this.cart) {
        if (item.status === 6 && (!item.quantity_scheduled || item.quantity_scheduled <= 0)) {
          this.submitError = `Item ${item.product_sku}: cannot be SHIPPED without schedule.`; return;
        }
        if (item.status === 3 && (!item.quantity_scheduled || item.quantity_scheduled <= 0)) {
          this.submitError = `Item ${item.product_sku}: cannot be RECEIVED without schedule.`; return;
        }
      }

      this.submitting = true;
      try {
        const headerRes = await new Promise((resolve, reject) => {
          Requestor.postAsyncP("/api/sales/record_transfer_order/", {
            ordering_location: this.orderData.ordering_location,
            selling_location: this.orderData.selling_location,
            status: 1,
            delivery_date: moment().add(7, 'days').format('MM-DD-YYYY'),
            schedule_date: moment().format('MM-DD-YYYY'),
            truck: null,
            created_by: this.userDetails.username,
          }, this, resolve, reject);
        });

        if (!headerRes.data || !headerRes.data.result || !headerRes.data.result.length) {
          this.submitError = (headerRes.data && headerRes.data.error) || 'Header failed.';
          return;
        }
        const orderId = headerRes.data.result[0].id;

        const itemsPayload = this.cart.map(item => ({
          product_sku:        item.product_sku,
          product_name:       item.product_name,
          product_id:         item.product_id,
          quantity:           parseInt(item.quantity) || 0,
          quantity_scheduled: parseInt(item.quantity_scheduled) || 0,
          quantity_shipped:   parseInt(item.quantity_shipped) || 0,
          quantity_received:  parseInt(item.quantity_received) || 0,
          wholesale_price:    parseFloat(item.wholesale_price) || 0,
          status:             parseInt(item.status) || 1,
          delivery_status:    parseInt(item.delivery_status) || 0,
          delivery_date:      item.requested_delivery_date || null,
          schedule_date:      item.schedule_date || null,
          received_date:      item.received_date || null,
          locsub:             item.locsub,
          reserved_locsub:    item.reserved_locsub,
          shipping_location:  '47',
          comments:           item.comments || null,
          images:             item.images || [],
        }));

        const itemsRes = await new Promise((resolve, reject) => {
          Requestor.postAsyncP("/api/sales/save_transfer_order_items/", {
            id: orderId,
            username: this.userDetails.username,
            items: itemsPayload,
          }, this, resolve, reject);
        });

        if (itemsRes.data && itemsRes.data.error) {
          this.submitError = itemsRes.data.error;
          return;
        }

        this.$bvToast.toast('Store order created successfully!', { variant: 'success', autoHideDelay: 2500 });
        this.$emit('order-completed');
        this.cart = [];
      } catch (e) {
        console.error(e);
        this.submitError = 'Unexpected error creating order.';
      } finally {
        this.submitting = false;
      }
    },
  }
}
</script>

<style scoped>
/* keep ALL styles from your original file — unchanged */
.store-order-form {
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C; padding: 4px; font-size: 15px;
  --so-orange: #EE7A22; --so-orange-dark: #D4620F; --so-orange-soft: #FFF1E4;
  --so-teal: #0F6E6B; --so-teal-soft: #E2F2F0;
  --so-ink: #221F1C; --so-ink-2: #34302B;
  --so-canvas: #F6F4F0; --so-mist: #F1EEE9; --so-line: #E5E1DA;
  --so-muted: #736C63; --so-danger: #C93C30; --so-danger-soft: #FBE9E7;
  --so-warn: #C98A12; --so-warn-soft: #FFF4DC;
  --so-head: 'Sora', 'Manrope', system-ui, sans-serif;
}
.text-right { text-align: right; }
.text-center { text-align: center; }
.text-muted { color: var(--so-muted) !important; }
.text-danger { color: var(--so-danger) !important; }
.text-success { color: var(--so-teal) !important; }
.d-block { display: block !important; }
.store-order-form .vs-strip { display: flex; flex-wrap: wrap; background: #fff; border: 1px solid var(--so-line); border-radius: 14px; overflow: hidden; }
.store-order-form .vs-strip-cell { flex: 1 1 180px; padding: 14px 20px; border-right: 1px solid var(--so-line); display: flex; flex-direction: column; justify-content: center; }
.store-order-form .vs-strip-cell:last-child { border-right: 0; }
.store-order-form .vs-strip-cell span { display: block; font-size: 11px; color: var(--so-muted); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
.store-order-form .vs-strip-cell strong { font-family: var(--so-head); font-size: 18px; font-weight: 700; color: var(--so-ink); }
.order-view-tabs >>> .nav-tabs { border-bottom: 3px solid #E5E1DA; gap: 4px; }
.order-view-tabs >>> .nav-tabs .nav-link { font-weight: 700; font-size: 14px; color: #736C63; padding: 10px 18px; border-radius: 10px 10px 0 0; border: 1.5px solid #E5E1DA; border-bottom: none; background: #F7F5F2; }
.order-view-tabs >>> .nav-tabs .nav-link.active { color: #fff; border-color: transparent; }
.order-view-tabs >>> .nav-tabs .nav-link.active.is-product { background: #221F1C; }
.order-view-tabs >>> .nav-tabs .nav-link.active.is-order   { background: #0F6E6B; }
.order-view-tabs >>> .tab-content { border: 1.5px solid #E5E1DA; border-top: none; border-radius: 0 0 12px 12px; background: #fff; }
.store-order-form .vs-btn-primary.btn, .store-order-form .vs-btn-ghost.btn { border-radius: 10px !important; font-weight: 700; font-size: 14px; padding: 8px 16px; display: inline-flex; align-items: center; gap: 7px; box-shadow: none !important; }
.store-order-form .vs-btn-primary.btn { background: var(--so-orange) !important; border: 1.5px solid var(--so-orange) !important; color: #fff !important; }
.store-order-form .vs-btn-primary.btn:hover:not(:disabled) { background: var(--so-orange-dark) !important; border-color: var(--so-orange-dark) !important; }
.store-order-form .vs-btn-ghost.btn { background: #fff !important; color: var(--so-ink) !important; border: 1.5px solid var(--so-line) !important; }
.store-order-form .vs-btn-ghost.btn:hover:not(:disabled) { border-color: var(--so-orange) !important; color: var(--so-orange-dark) !important; }
.store-order-form .vs-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; background: var(--so-mist); color: var(--so-ink-2); white-space: nowrap; }
.store-order-form .vs-chip.is-teal   { background: var(--so-teal-soft);   color: var(--so-teal); }
.store-order-form .vs-chip.is-warn   { background: var(--so-warn-soft);   color: #8A5E09; }
.store-order-form .vs-chip.is-orange { background: var(--so-orange-soft); color: var(--so-orange-dark); }
.store-order-form .vs-chip.is-danger { background: var(--so-danger-soft); color: var(--so-danger); }
.pos-search-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.pos-search-input { max-width: 100%; width: 100%; }
.pos-search-input >>> .form-control { height: 42px; font-size: 15px; border-radius: 10px 0 0 10px !important; border: 1.5px solid var(--so-line); }
.pos-search-input >>> .input-group-append .btn { border-radius: 0 10px 10px 0 !important; border: 1.5px solid var(--so-line); border-left: none; padding: 0 16px; }
.product-cards-container { padding: 0; }
.product-cards-grid { display: flex; flex-direction: column; gap: 10px; }
.product-card { display: flex; border: 1px solid var(--so-line); border-radius: 14px; overflow: hidden; background: #fff; transition: border-color 0.15s ease; }
.product-card:hover { border-color: var(--so-orange); }
.product-card.out-of-stock { opacity: 0.7; }
.product-card-info { flex: 1; padding: 14px 18px; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
.product-card-info .product-sku { font-size: 13px; font-weight: 700; color: var(--so-orange-dark); font-family: 'Menlo', monospace; }
.product-card-info .product-name { font-family: var(--so-head); font-size: 15px; font-weight: 700; color: var(--so-ink); margin-top: 2px; line-height: 1.3; }
.product-card-actions { width: 170px; min-width: 170px; padding: 14px 18px; display: flex; flex-direction: column; justify-content: center; gap: 10px; background: #FAF9F7; border-left: 1px solid #F0ECE6; }
.product-card-actions .price-row { display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; }
.product-card-actions .price-label { color: var(--so-muted); font-weight: 600; font-size: 12px; }
.product-card-actions .current-price { font-family: var(--so-head); font-size: 16px; font-weight: 800; color: var(--so-ink); }
.vs-fields { display: grid; gap: 12px 16px; }
.vs-field { display: flex; flex-direction: column; gap: 5px; margin: 0; min-width: 0; }
.vs-field > span { font-size: 12.5px; font-weight: 700; color: var(--so-muted); text-transform: uppercase; letter-spacing: 0.04em; }
.vs-field-input { border: 1.5px solid var(--so-line) !important; border-radius: 10px !important; height: 38px !important; font-size: 14px !important; font-weight: 600 !important; background: #fff !important; color: var(--so-ink) !important; }
.pos-totals-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; background: var(--so-ink); color: #fff; padding: 14px 24px; box-shadow: 0 -4px 18px -10px rgba(34, 31, 28, 0.4); }
.pos-totals-stats { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 32px; }
.pos-totals-item { display: flex; align-items: center; gap: 10px; }
.pos-totals-item > .b-icon { font-size: 22px; color: #FFB06B; }
.pos-totals-item span { display: block; font-size: 12.5px; font-weight: 600; color: rgba(255, 255, 255, 0.58); }
.pos-totals-item strong { font-family: var(--so-head); font-size: 19px; font-weight: 700; }
.pos-totals-item.is-grand strong { font-size: 24px; color: #FFB06B; }
.pos-totals-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pos-foot-note { font-size: 13px; color: rgba(255, 255, 255, 0.6); font-weight: 500; }
.ord-product-list { display: flex; flex-direction: column; gap: 14px; }
.ord-product-card { display: grid; grid-template-columns: 200px 1fr 1.2fr 1.5fr 0.7fr 60px; gap: 8px; background: #fff; border: 1px solid var(--so-line); border-left: 5px solid var(--so-line); border-radius: 16px; padding: 10px; align-items: stretch; }
.ord-product-card.is-status-1 { border-left-color: #C98A12; }
.ord-product-card.is-status-2 { border-left-color: #2B83B5; }
.ord-product-card.is-status-3 { border-left-color: #0F6E6B; }
.ord-product-card.is-status-4 { border-left-color: #D4620F; }
.ord-product-card.is-status-5 { border-left-color: #221F1C; }
.ord-product-card.is-status-6 { border-left-color: #8A5E09; }
.ord-product-img { position: relative; border-radius: 12px; background: linear-gradient(160deg, #F7F3EE, #EFEAE3); display: flex; align-items: center; justify-content: center; overflow: hidden; min-height: 100px; }
.ord-product-img img { width: 100%; height: 100%; max-height: 120px; object-fit: contain; }
.ord-product-img-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #A59D92; cursor: pointer; padding: 10px; }
.ord-product-img-empty span { font-size: 10px; font-weight: 700; text-transform: uppercase; }
.ord-product-img-remove { position: absolute; top: 4px; right: 4px; }
.ord-product-panel { background: #FAF9F7; border-radius: 12px; padding: 10px 12px; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.ord-panel-title { font-family: var(--so-head); font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: var(--so-muted); margin: 0; }
.ord-product-info { background: #F7F5F2; }
.ord-product-sku { font-family: 'Menlo', monospace; font-size: 11.5px; font-weight: 700; color: var(--so-orange-dark); }
.ord-product-name { font-family: var(--so-head); font-size: 14px; font-weight: 700; color: var(--so-ink); line-height: 1.3; word-break: break-word; }
.ord-product-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.ord-product-sub { font-size: 11.5px; color: var(--so-muted); display: flex; flex-direction: column; gap: 2px; }
.ord-product-sub b { color: var(--so-ink); }
.ord-avail { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; margin-top: auto; }
.ord-avail-item { background: #F1EEE9; border-radius: 8px; padding: 6px 4px; text-align: center; }
.ord-avail-item span { display: block; font-size: 9.5px; font-weight: 800; color: var(--so-muted); text-transform: uppercase; }
.ord-avail-item b { font-family: var(--so-head); font-size: 15px; color: var(--so-ink); }
.ord-avail-item.is-warn { background: var(--so-warn-soft); }
.ord-avail-item.is-warn b { color: #8A5E09; }
.ord-avail-item.is-good { background: var(--so-teal-soft); }
.ord-avail-item.is-good b { color: var(--so-teal); }
.ord-qty-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 12px; align-content: start; }
.ord-qty-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ord-qty-field > span { font-size: 10px; font-weight: 800; color: var(--so-muted); text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ord-qty-field >>> .form-control { text-overflow: ellipsis; }
.ord-qty-field >>> .mx-datepicker { width: 100%; }
.ord-product-price .ord-price-total { display: flex; justify-content: space-between; align-items: baseline; padding-top: 6px; border-top: 1px dashed var(--so-line); margin-top: 4px; }
.ord-product-price .ord-price-total span { font-size: 10.5px; font-weight: 800; color: var(--so-muted); text-transform: uppercase; }
.ord-product-price .ord-price-total strong { font-family: var(--so-head); font-size: 14px; font-weight: 800; color: var(--so-orange-dark); }
.ord-product-actions { background: #FFF6F4; border: 1px solid #F3C9C2; display: flex; flex-direction: column; justify-content: center; gap: 6px; padding: 8px; }
.ord-product-actions .btn { padding: 4px 0 !important; font-size: 12px !important; border-radius: 8px !important; }
.store-order-form .mx-datepicker { width: 100%; }
.store-order-form .mx-input { height: 32px !important; font-size: 12px !important; border-radius: 8px !important; border: 1.5px solid var(--so-line) !important; padding: 2px 26px 2px 8px !important; }
@media (max-width: 1500px) { .ord-product-card { grid-template-columns: 90px 1fr 1fr 1fr 1fr 60px; } }
@media (max-width: 1280px) { .ord-product-card { grid-template-columns: 90px 1fr 1fr 1fr; } .ord-product-locs { grid-column: 2 / 4; } .ord-product-price { grid-column: 1 / 3; } .ord-product-actions { grid-column: 3 / 5; flex-direction: row; } .ord-product-actions .btn { flex: 1; } }
@media (max-width: 991px) { .store-order-form .vs-card { padding: 14px; } .product-card-actions { width: 120px; min-width: 120px; } .ord-product-card { grid-template-columns: 90px 1fr; } .ord-product-qty, .ord-product-locs, .ord-product-price { grid-column: 1 / -1; } .ord-product-actions { grid-column: 1 / -1; flex-direction: row; } .ord-product-actions .btn { flex: 1; } }
@media (max-width: 767px) { .product-card { flex-direction: column; } .product-card-actions { width: 100%; min-width: 100%; border-left: 0; border-top: 1px solid #F0ECE6; flex-direction: row; align-items: center; } .ord-product-card { grid-template-columns: 1fr; } .ord-product-img { min-height: 160px; } }
</style>

<style>
.store-order-form .mx-datepicker-popup { z-index: 3000; }
</style>


// WEBPACK FOOTER //
// src/components/TransferOrders/NewStoreOrderForm.vue